import {Injectable} from "@angular/core";
import {AddPromotionUseCase} from "@app/core/cart/application/add-promotion.use-case";
import {IsPromotionAddedUseCase} from "@app/core/cart/application/is-promotion-added.use-case";
import {RemovePromotionUseCase} from "@app/core/cart/application/remove-promotion.use-case";
import {IsPriceHiddenUseCase} from "@app/core/delivery-point/application/is-price-hidden.use-case";
import {Product} from "@app/core/product/domain/dtos/product";
import {GetPromotionDetailUseCase} from "@app/core/promotion/application/get-promotion-detail.use-case";
import {PromotionDetail} from "@app/core/promotion/domain/dtos/promotion-detail";
import {NoStockError} from "@app/core/stock/domain/errors/no-stock.error";
import {IsLoggedInUseCase} from "@app/core/user/application/isLoggedIn.use-case";
import {BehaviorSubject, Observable, Subscriber} from "rxjs";
import {take} from "rxjs/operators";
import {PopupNotifier} from "../errors/popup.notifier";

@Injectable()
export class AddPromotionToCartButtonViewModel {

    isLoggedIn$ = this.isLoggedInUseCase.execute();
    isPriceHidden$ = this.isPriceHiddenUseCase.execute();

    public notifier = new PopupNotifier();
    private notEnoughStockEmitter!: Subscriber<Product[]>;
    private isAddedSubject = new BehaviorSubject<boolean>(false);
    public isAdded$: Observable<boolean> = this.isAddedSubject.asObservable();
    public notEnoughStock$: Observable<Product[]> = new Observable<Product[]>(emitter => this.notEnoughStockEmitter = emitter);

    private isAddPromotionToCartLoadingSubject = new BehaviorSubject(false)
    readonly isAddPromotionToCartLoading$ = this.isAddPromotionToCartLoadingSubject.asObservable();


    constructor(
        private isLoggedInUseCase: IsLoggedInUseCase,
        private isPriceHiddenUseCase: IsPriceHiddenUseCase,
        private getPromotionDetailUseCase: GetPromotionDetailUseCase,
        private addPromotionUseCase: AddPromotionUseCase,
        private removePromotionUseCase: RemovePromotionUseCase,
        private isPromotionAddedUseCase: IsPromotionAddedUseCase,
    ) { }

    addPromotionToCart(promotionCode: string): void {
        if (this.isAddPromotionToCartLoadingSubject.value) {
            return;
        }
      this.isAddPromotionToCartLoadingSubject.next(true);
        // Get promotion details data, since promotion list item does not have products info
        this.getPromotionDetailUseCase.execute(promotionCode)
            .pipe(
                // Avoid receiving emissions after the first call
                take(1),
            ).subscribe({
                next: promotionDetail => {
                    this.isAddPromotionToCartLoadingSubject.next(false);
                    // Add it to the cart
                    this.addPromotion(promotionDetail);
                },
                error: error => {
                    this.notifier.notifyError(error);
                    this.isAddPromotionToCartLoadingSubject.next(false);
                }
            });
    }

    removePromotionFromCart(promotionCode: string): void {
        this.removePromotionUseCase.execute(promotionCode).subscribe({
            next: cartLineDBs => {
                if (cartLineDBs)
                    // Mark as not added
                    this.isAddedSubject.next(false);
            },
            error: error => {
                this.notifier.notifyError(error);
            }
        });
    }

    isPromotionAdded(promotionCode: string): void {
        this.isPromotionAddedUseCase.execute(promotionCode).subscribe({
            next: added => {
                this.isAddedSubject.next(added);
            },
            error: error => {
                this.notifier.notifyError(error);
            }
        });
    }

    private addPromotion(promotionDetail: PromotionDetail): void {
        this.isAddPromotionToCartLoadingSubject.next(true);
        this.addPromotionUseCase.execute(promotionDetail)
            .pipe(take(1))
            .subscribe({
                next: cartLineDBs => {
                    if (cartLineDBs) {
                        // Mark as added
                        this.isAddedSubject.next(true);
                    }
                    this.isAddPromotionToCartLoadingSubject.next(false);
                },
                error: error => {
                    if (error instanceof NoStockError) {
                        // Notify which products don't have enough stock
                        this.notEnoughStockEmitter.next(error.noStockProducts);
                    } else {
                        // Notify other errors
                        this.notifier.notifyError(error);
                    }
                    this.isAddPromotionToCartLoadingSubject.next(false);
                }
            });
    }
}
